import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, Stack } from '@mui/material';
import { Analytics, ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { designTokens } from '../../theme/designTokens';
import CustomModal from '../CustomModal';
import RecordSelectionStep from './RecordSelectionStep';
import AnalysisStep from './AnalysisStep';
import {
  selectIsModalOpen,
  selectCurrentStep,
  selectSelectedRowIds,
  closeAnalysisModal,
  proceedToAnalysisStep,
  goBackToSelectionStep
} from '../../features/analysis/analysisSlice';

const AnalysisModal = ({ queryResultData }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isModalOpen = useSelector(selectIsModalOpen);
  const currentStep = useSelector(selectCurrentStep);
  const selectedRowIds = useSelector(selectSelectedRowIds);

  const handleClose = () => {
    dispatch(closeAnalysisModal());
  };

  const handleContinue = () => {
    dispatch(proceedToAnalysisStep());
  };

  const handleGoBack = () => {
    dispatch(goBackToSelectionStep());
  };

  const getModalTitle = () => {
    return currentStep === 1 ? 'Select Records for Analysis' : 'Analyze Selected Records';
  };

  const getModalSubtitle = () => {
    return currentStep === 1
      ? 'Choose the records you want to analyze with AI-powered insights'
      : 'Review and analyze your selected records with predefined prompts';
  };

  const getModalActions = () => {
    console.log('AnalysisModal - selectedRowIds:', selectedRowIds);

    if (currentStep === 1) {
      // Step 1: Continue button
      return (
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: designTokens.fontWeight.medium,
              fontSize: designTokens.fontSize.sm,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={selectedRowIds.length === 0}
            sx={{
              textTransform: 'none',
              fontWeight: designTokens.fontWeight.semibold,
              fontSize: designTokens.fontSize.sm,
            }}
          >
            Continue with {selectedRowIds.length} Record{selectedRowIds.length !== 1 ? 's' : ''}
          </Button>
        </Stack>
      );
    } else {
      // Step 2: Back and Close buttons
      return (
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleGoBack}
            sx={{
              textTransform: 'none',
              fontWeight: designTokens.fontWeight.medium,
              fontSize: designTokens.fontSize.sm,
            }}
          >
            Back to Selection
          </Button>
          <Button
            variant="contained"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              fontWeight: designTokens.fontWeight.semibold,
              fontSize: designTokens.fontSize.sm,
            }}
          >
            Close
          </Button>
        </Stack>
      );
    }
  };

  return (
    <CustomModal
      open={isModalOpen}
      onClose={handleClose}
      title={getModalTitle()}
      subtitle={getModalSubtitle()}
      icon={<Analytics sx={{ color: 'white', fontSize: 24 }} />}
      maxWidth="xl"
      fullWidth
      contentPadding={false}
      actions={getModalActions()}
    >
      <Box sx={{
        height: 'calc(100vh - 200px)',
        maxHeight: '800px',
        minHeight: '500px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {currentStep === 1 && (
          <RecordSelectionStep queryResultData={queryResultData} />
        )}
        {currentStep === 2 && (
          <AnalysisStep />
        )}
      </Box>
    </CustomModal>
  );
};

export default AnalysisModal;